import { useEffect, useRef, useState } from 'react';
import { Box, Paper, Typography, Stack } from '@mui/material';
import { 
	Clear, 
	Draw, 
	// Edit, 
	Upload } from '@mui/icons-material';
import SignaturePad from 'signature_pad';
import { TextButton } from './ui/Buttons';
import { StyledTextBox } from './ui/TextBox';

export default function SignaturePadCanvas({ onChange, width = 400, height = 200 }) {
	const [signatureMethod, setSignatureMethod] = useState('draw');
	const [typedSignature, setTypedSignature] = useState('');
	const canvasRef = useRef(null);
	const signaturePadRef = useRef(null);
	const fileInputRef = useRef(null);

	useEffect(() => {
		if (!canvasRef.current) return;

		const canvas = canvasRef.current;
		canvas.width = width;
		canvas.height = height;

		signaturePadRef.current = new SignaturePad(canvas, {
			backgroundColor: 'rgb(255, 255, 255)',
			penColor: 'rgb(0, 0, 0)',
			minWidth: 1,
			maxWidth: 3
		});

		return () => {
			if (signaturePadRef.current) {
				signaturePadRef.current.off();
			}
		};
	}, [signatureMethod, width, height, onChange]);

	useEffect(() => {
		if (!signaturePadRef.current || !onChange) return;

		const handleEnd = () => {
			const signature = signaturePadRef.current?.toDataURL() || '';
			onChange(signature);
		};

		signaturePadRef.current.addEventListener('endStroke', handleEnd);

		return () => {
			signaturePadRef.current?.removeEventListener('endStroke', handleEnd);
		};
	}, [signatureMethod, width, height, onChange]);

	const handleClear = () => {
		signaturePadRef.current?.clear();
		setTypedSignature('');
		if (onChange) {
			onChange('');
		}
	};

	const handleTypeSignature = (e) => {
		const value = e.target.value;
		setTypedSignature(value);
		if (onChange) {
			onChange(value);
		}
	};

	const handleUploadSignature = (e) => {
		const file = e.target.files?.[0];
		if (!file || !onChange) return;

		const reader = new FileReader();
		reader.onload = (event) => {
			const signature = event.target?.result;
			onChange(signature);
		};
		reader.readAsDataURL(file);
	};

	return (
		<Paper elevation={2} sx={{ p: 3, backgroundColor: '#fafafa' }}>
			<Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
				Add Your Signature
			</Typography>

			<Stack direction="row" spacing={2} sx={{ mb: 1 }}>
				<TextButton
					onClick={() => setSignatureMethod('draw')}
					variant={signatureMethod === 'draw' ? 'outlined' : 'text'}
					startIcon={<Draw />}
				>
					Draw
				</TextButton>
				{/* <TextButton
					onClick={() => setSignatureMethod('type')}
					variant={signatureMethod === 'type' ? 'outlined' : 'text'}
					startIcon={<Edit />}
				>
					Type
				</TextButton> */}
				<TextButton
					onClick={() => setSignatureMethod('upload')}
					variant={signatureMethod === 'upload' ? 'outlined' : 'text'}
					startIcon={<Upload />}
				>
					Upload
				</TextButton>
			</Stack>


			{signatureMethod === 'draw' && (
				<Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
					<Paper
						elevation={1}
						sx={{
							p: 1,
							border: '2px solid #e0e0e0',
							borderRadius: 2,
							backgroundColor: 'white'
						}}
					>
						<canvas
							ref={canvasRef}
							style={{
								display: 'block',
								touchAction: 'none',
								cursor: 'crosshair'
							}}
						/>
					</Paper>
					<Stack direction="row" spacing={2}>
						<TextButton
							onClick={handleClear}
							variant="outlined"
							startIcon={<Clear />}
						>
							Clear Signature
						</TextButton>
						{/* <ContainedButton
							onClick={handleClear}
							variant="outlined"
						>
							Apply Signature
						</ContainedButton> */}
					</Stack>

				</Box>
			)}

			{signatureMethod === 'type' && (
				<Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
					<StyledTextBox
						fullWidth
						value={typedSignature}
						onChange={handleTypeSignature}
						placeholder="Type your signature"
						variant="outlined"
					/>
					<TextButton
						onClick={handleClear}
						variant="outlined"
						startIcon={<Clear />}
						sx={{ alignSelf: 'flex-start' }}
					>
						Clear Signature
					</TextButton>
				</Box>
			)}

			{signatureMethod === 'upload' && (
				<Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
					<input
						ref={fileInputRef}
						type="file"
						accept="image/*"
						onChange={handleUploadSignature}
						style={{
							padding: '12px',
							border: '1px solid #ccc',
							borderRadius: '4px',
							fontSize: '16px',
							width: '100%'
						}}
					/>
					<TextButton
						onClick={() => {
							if (fileInputRef.current) {
								fileInputRef.current.value = '';
							}
							handleClear();
						}}
						variant="outlined"
						startIcon={<Clear />}
						sx={{ alignSelf: 'flex-start' }}
					>
						Clear Signature
					</TextButton>
				</Box>
			)}
		</Paper>
	);
}
